import { Timestamp, collection, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import { db } from '../../lib/firebase';
import { storage } from '../../lib/firebase';
import type { ThreadAttachment } from './types';
import { assertWorkspaceInvariant } from '../../utils/workspaceInvariant';

export type ThreadTempFileDoc = {
  id: string; // attachmentId
  threadId: string;
  // 保存先（追記post / comment）
  targetType: 'post' | 'comment';
  targetId: string;
  // legacy: 旧実装では postId のみ
  postId?: string;
  fileName: string;
  mimeType: string;
  size: number;
  storagePath: string;
  downloadURL: string;
  expiresAt: any;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
  updatedBy: string;
  isDeleted?: boolean;
  deletedAt?: any;
  deletedBy?: string;
};

export const threadTempFilesCollectionRef = (workspaceId: string) =>
  collection(db, `workspaces/${workspaceId}/threadTempFiles`);

export const upsertThreadTempFileIndex = async (args: {
  workspaceId: string;
  userId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  attachment: ThreadAttachment; // attachmentType='temporary' を想定
}): Promise<void> => {
  const { workspaceId, userId, threadId, targetType, targetId, attachment } = args;
  assertWorkspaceInvariant(workspaceId, { source: 'threadTempFiles.upsertThreadTempFileIndex' });
  const ref = doc(threadTempFilesCollectionRef(workspaceId), attachment.id);
  await setDoc(
    ref,
    {
      id: attachment.id,
      threadId,
      targetType,
      targetId,
      // legacy field for existing clients (posts only)
      postId: targetType === 'post' ? targetId : undefined,
      fileName: attachment.fileName,
      mimeType: attachment.mimeType,
      size: attachment.size,
      storagePath: attachment.storagePath,
      downloadURL: attachment.downloadURL,
      expiresAt: attachment.expiresAt ?? null,
      createdAt: serverTimestamp(),
      createdBy: userId,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
      isDeleted: false,
    } satisfies ThreadTempFileDoc,
    { merge: true }
  );
};

export const markThreadTempFileDeleted = async (args: {
  workspaceId: string;
  attachmentId: string;
  userId: string;
}): Promise<void> => {
  const { workspaceId, attachmentId, userId } = args;
  assertWorkspaceInvariant(workspaceId, { source: 'threadTempFiles.markThreadTempFileDeleted' });
  const ref = doc(threadTempFilesCollectionRef(workspaceId), attachmentId);
  await updateDoc(ref, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  } as any);
};

export const deleteThreadTempFileByAttachmentId = async (args: {
  workspaceId: string;
  attachmentId: string;
  userId: string;
}): Promise<{ threadId: string; targetType: 'post' | 'comment'; targetId: string; attachmentId: string; fileName: string } | null> => {
  const { workspaceId, attachmentId, userId } = args;
  assertWorkspaceInvariant(workspaceId, { source: 'threadTempFiles.deleteThreadTempFileByAttachmentId' });
  const refDoc = doc(threadTempFilesCollectionRef(workspaceId), attachmentId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) return null;
  const data = snap.data() as ThreadTempFileDoc;
  const storagePath = String((data as any)?.storagePath ?? '');

  if (storagePath) {
    try {
      await deleteObject(storageRef(storage, storagePath));
    } catch (e) {
      // Storage 側の削除失敗は続行（一覧・表示側で復旧できるように）
      console.warn('[deleteThreadTempFileByAttachmentId] storage delete failed:', e);
    }
  }

  await markThreadTempFileDeleted({ workspaceId, attachmentId, userId });

  // スレッド側の添付表示を削除プレースホルダに置換
  try {
    const threadId = String((data as any)?.threadId ?? '');
    const targetType = (String((data as any)?.targetType ?? '') as any) || 'post';
    const targetId = String((data as any)?.targetId ?? (data as any)?.postId ?? '');
    const docPath =
      targetType === 'comment'
        ? `workspaces/${workspaceId}/threads/${threadId}/comments/${targetId}`
        : `workspaces/${workspaceId}/threads/${threadId}/posts/${targetId}`;
    const targetRef = doc(db, docPath);
    const targetSnap = await getDoc(targetRef);
    if (targetSnap.exists()) {
      const targetData = targetSnap.data() as any;
      const attachments = Array.isArray(targetData.attachments) ? targetData.attachments : [];
      const next = attachments.map((a: any) => {
        if (!a) return a;
        const id = String(a.id ?? '');
        const type = String(a.attachmentType ?? '');
        if (type === 'temporary' && id === attachmentId) {
          return {
            ...a,
            isDeleted: true,
            // serverTimestamp は配列要素内で使えないためクライアント時刻を使用
            deletedAt: Timestamp.now(),
            deletedBy: userId,
            downloadURL: '',
          };
        }
        return a;
      });
      await updateDoc(targetRef, {
        attachments: next,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      } as any);
    }
  } catch (e) {
    console.warn('[deleteThreadTempFileByAttachmentId] failed to update thread target attachments:', e);
  }

  return {
    threadId: String((data as any)?.threadId ?? ''),
    targetType: (String((data as any)?.targetType ?? '') as any) || 'post',
    targetId: String((data as any)?.targetId ?? (data as any)?.postId ?? ''),
    attachmentId,
    fileName: String((data as any)?.fileName ?? ''),
  };
};

